import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Copy, CheckCircle2, Loader2, AlertTriangle, ShieldAlert, ScrollText, Filter, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtDateTime } from "../lib/dateFormat";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import EmptyState from "../components/EmptyState";

// ============================================================
// /superadmin/reset-requests — lista de solicitudes pendientes
// ============================================================
export function SuperadminResetRequests() {
    const { user, bootstrapping } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (!bootstrapping && user && user.role !== "superadmin") navigate("/panel", { replace: true });
    }, [user, bootstrapping, navigate]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState("");

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const r = await api.get("/superadmin/reset-requests?status=pending");
            setRows(r.data || []);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const copyUrl = async (url, id) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(id);
            setTimeout(() => setCopied(""), 2000);
        } catch { /* ignore */ }
    };
    const markFulfilled = async (id) => {
        try {
            await api.post(`/superadmin/reset-requests/${id}/mark-fulfilled`);
            load();
        } catch (e) { setErr(formatApiError(e)); }
    };

    return (
        <div className="px-4 md:px-8 py-6 space-y-5" data-testid="reset-requests-page">
            <header>
                <h1 className="font-display text-2xl font-bold flex items-center gap-2">
                    <KeyRound size={20} className="text-brand-orange"/>
                    Solicitudes de recuperación
                </h1>
                <p className="text-sm text-slate-400 mt-1">Enlaces activos generados por usuarios. Cópialos y compártelos vía WhatsApp o el canal que uses.</p>
            </header>
            {loading ? <Loader2 className="animate-spin text-slate-500"/> :
             err ? <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-3 text-sm text-red-200" data-testid="reset-req-err">{err}</div> :
             rows.length === 0 ? (
                <EmptyState icon={KeyRound}
                    title="No hay solicitudes pendientes"
                    secondaryHint="Todo está tranquilo."
                    testid="reset-req-empty"/>
             ) : (
                <ul className="space-y-3" data-testid="reset-req-list">
                    {rows.map(r => (
                        <li key={r.id} data-testid={`reset-req-${r.id}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-2">
                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div>
                                    <div className="font-medium text-slate-100">{r.user?.full_name || "—"}</div>
                                    <div className="text-xs text-slate-400 font-mono">{r.email}</div>
                                </div>
                                <div className="text-[11px] text-slate-500">Creado {fmtDateTime(r.created_at)}</div>
                            </div>
                            <div className="text-xs bg-slate-950 border border-slate-800 rounded p-2 font-mono break-all" data-testid={`reset-req-url-${r.id}`}>
                                {r.reset_url}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button type="button" onClick={() => copyUrl(r.reset_url, r.id)} data-testid={`reset-req-copy-${r.id}`}
                                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs">
                                    {copied === r.id ? <CheckCircle2 size={12} className="text-emerald-400"/> : <Copy size={12}/>}
                                    {copied === r.id ? "Copiado" : "Copiar enlace"}
                                </button>
                                <button type="button" onClick={() => markFulfilled(r.id)} data-testid={`reset-req-fulfill-${r.id}`}
                                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-700 hover:border-brand-orange text-slate-300 text-xs">
                                    Marcar como entregado
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
             )}
        </div>
    );
}

// ============================================================
// /superadmin/wipe — Wipe DB doble confirmación
// ============================================================
export function SuperadminWipe() {
    const { user, bootstrapping } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (!bootstrapping && user && user.role !== "superadmin") navigate("/panel", { replace: true });
    }, [user, bootstrapping, navigate]);

    const [confirm, setConfirm] = useState("");
    const [keepConfig, setKeepConfig] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);
    const REQUIRED = "BORRAR TODOS LOS DATOS";
    const canWipe = confirm === REQUIRED;

    const doWipe = async () => {
        if (!window.confirm("¿Estás 100% seguro? Esta acción NO se puede deshacer.")) return;
        setBusy(true); setErr("");
        try {
            const r = await api.post("/superadmin/wipe-all", {
                confirmation: "WIPE-ALL-DATA-YES",
                keep_config: keepConfig,
            });
            setResult(r.data);
            setConfirm("");
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="px-4 md:px-8 py-6 space-y-5 max-w-3xl" data-testid="wipe-page">
            <header>
                <h1 className="font-display text-2xl font-bold flex items-center gap-2 text-red-300">
                    <ShieldAlert size={22}/> Borrar todos los datos
                </h1>
            </header>
            <div className="rounded-xl border-2 border-red-500/60 bg-red-500/10 p-4 text-sm text-red-100 space-y-2" data-testid="wipe-warning">
                <div className="font-bold flex items-center gap-2"><AlertTriangle size={16}/> Esta acción BORRA todos los datos operativos</div>
                <p>Trabajos, activos, operadores, clientes, cargas de combustible, lecturas, casetas, gastos, mantenimiento, viajes, asistencia, fotos, salarios y solicitudes de recuperación.</p>
                <p>Los <strong>usuarios NO se borran</strong>. Los <strong>audit_logs se preservan</strong> para trazabilidad. Esta acción <strong>NO se puede deshacer</strong>.</p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                <input type="checkbox" checked={keepConfig} onChange={e => setKeepConfig(e.target.checked)}
                    data-testid="wipe-keep-config" className="accent-brand-orange"/>
                Mantener configuración base (categorías, materiales, checklists, subtipos)
            </label>
            <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                    Escribe exactamente <span className="font-mono text-red-300">{REQUIRED}</span> para confirmar
                </label>
                <input type="text" value={confirm} onChange={e => setConfirm(e.target.value)}
                    data-testid="wipe-confirm-input" autoComplete="off"
                    className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3 font-mono"/>
            </div>
            {err && <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-3 text-sm text-red-200" data-testid="wipe-err">{err}</div>}
            {result && (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-4 text-sm text-emerald-100" data-testid="wipe-result">
                    <div className="font-bold mb-2">✔ Wipe completado</div>
                    <div className="text-xs font-mono space-y-0.5">
                        {Object.entries(result.deleted || {}).map(([k, v]) => (
                            <div key={k}>{k}: <span className="text-emerald-300">-{v}</span></div>
                        ))}
                        <div className="mt-2 text-slate-300">Preservados: users={result.kept?.users}, audit_logs={result.kept?.audit_logs}</div>
                    </div>
                </div>
            )}
            <button type="button" onClick={doWipe} disabled={!canWipe || busy}
                data-testid="wipe-submit"
                className="w-full h-12 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy && <Loader2 className="animate-spin" size={16}/>}
                Borrar todo
            </button>
        </div>
    );
}

// ============================================================
// /auditoria — tabla de audit logs
// ============================================================
function AuditDiffView({ diff, metadata }) {
    const before = (diff && diff.before) ? diff.before : null;
    const after = (diff && diff.after) ? diff.after : null;
    const hasBoth = before && after && Object.keys(before).length > 0 && Object.keys(after).length > 0;
    const renderKV = (obj) => Object.entries(obj || {}).map(([k, v]) => (
        <div key={k} className="text-xs py-1 border-b border-slate-800 last:border-b-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
            <div className="font-mono text-slate-100 break-words">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
        </div>
    ));
    return (
        <div className="space-y-3">
            {hasBoth ? (
                <div className="grid grid-cols-2 gap-3" data-testid="audit-diff-both">
                    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-red-300 font-semibold mb-1">Antes</div>
                        {renderKV(before)}
                    </div>
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold mb-1">Después</div>
                        {renderKV(after)}
                    </div>
                </div>
            ) : after && Object.keys(after).length > 0 ? (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="audit-diff-after">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold mb-1">Nuevo</div>
                    {renderKV(after)}
                </div>
            ) : before && Object.keys(before).length > 0 ? (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="audit-diff-before">
                    <div className="text-[10px] uppercase tracking-wider text-red-300 font-semibold mb-1">Eliminado</div>
                    {renderKV(before)}
                </div>
            ) : (
                <div className="text-xs text-slate-500 italic">Sin diff granular disponible.</div>
            )}
            {metadata && Object.keys(metadata).length > 0 && (
                <details className="text-xs" data-testid="audit-diff-metadata">
                    <summary className="cursor-pointer text-slate-400">Metadata</summary>
                    <pre className="mt-2 bg-slate-950 border border-slate-800 rounded p-2 text-slate-300 overflow-x-auto">
{JSON.stringify(metadata, null, 2)}
                    </pre>
                </details>
            )}
        </div>
    );
}

const ACTION_COLORS = {
    create: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    update: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    delete: "bg-red-500/15 text-red-300 border-red-500/40",
    login: "bg-slate-500/15 text-slate-300 border-slate-500/40",
    password_change: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    password_reset: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    export: "bg-violet-500/15 text-violet-300 border-violet-500/40",
    status_transition: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    other: "bg-slate-800 text-slate-300 border-slate-700",
};

export function AuditLogs() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [filters, setFilters] = useState({ actor_email: "", action: "", entity_type: "", date_from: "", date_to: "" });
    const [showFilters, setShowFilters] = useState(false);
    const [diffOpen, setDiffOpen] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const params = { page, page_size: 25 };
            for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
            const r = await api.get("/audit-logs", { params });
            setRows(r.data.rows || []);
            setTotal(r.data.total || 0);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, [page, filters]);
    useEffect(() => { load(); }, [load]);

    const setF = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };
    const clearFilters = () => { setPage(1); setFilters({ actor_email: "", action: "", entity_type: "", date_from: "", date_to: "" }); };
    const hasFilters = Object.values(filters).some(v => v);
    const totalPages = Math.max(1, Math.ceil(total / 25));

    return (
        <div className="px-4 md:px-8 py-6 space-y-5" data-testid="audit-page">
            <header>
                <h1 className="font-display text-2xl font-bold flex items-center gap-2">
                    <ScrollText size={20} className="text-brand-orange"/>
                    Auditoría
                    {user?.role === "superadmin" && <span className="text-xs text-brand-orange">★</span>}
                </h1>
                <p className="text-sm text-slate-400 mt-1">{user?.role === "admin" ? "Registro de todas las acciones (excluye superadmins)" : "Registro completo del sistema."}</p>
            </header>

            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <div className="flex items-center justify-between mb-2">
                    <button type="button" onClick={() => setShowFilters(s => !s)} data-testid="audit-filters-toggle"
                        className="text-xs uppercase tracking-wider text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
                        <Filter size={14}/> {showFilters ? "Ocultar filtros" : "Filtros"}
                    </button>
                    {hasFilters && (
                        <button type="button" onClick={clearFilters} data-testid="audit-clear-filters"
                            className="text-xs text-slate-400 hover:text-red-300 inline-flex items-center gap-1">
                            <X size={12}/> Limpiar
                        </button>
                    )}
                </div>
                {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-800">
                        <input placeholder="Correo del usuario" value={filters.actor_email} onChange={e => setF("actor_email", e.target.value)}
                            data-testid="audit-filter-email" className="h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm"/>
                        <select value={filters.action} onChange={e => setF("action", e.target.value)} data-testid="audit-filter-action"
                            className="h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm">
                            <option value="">Toda acción</option>
                            {["create","update","delete","login","password_change","password_reset","export","status_transition","other"].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                        <input placeholder="Tipo de entidad (job, asset, user…)" value={filters.entity_type} onChange={e => setF("entity_type", e.target.value)}
                            data-testid="audit-filter-entity" className="h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm"/>
                        <input type="date" value={filters.date_from} onChange={e => setF("date_from", e.target.value)} data-testid="audit-filter-from"
                            className="h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm text-slate-100"/>
                        <input type="date" value={filters.date_to} onChange={e => setF("date_to", e.target.value)} data-testid="audit-filter-to"
                            className="h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm text-slate-100"/>
                    </div>
                )}
            </section>

            {loading ? <Loader2 className="animate-spin text-slate-500 mx-auto"/> :
             err ? <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-3 text-sm text-red-200">{err}</div> :
             rows.length === 0 ? (
                <EmptyState icon={ScrollText}
                    title="No hay eventos que coincidan"
                    secondaryHint="Ajusta los filtros o el rango de fechas."
                    testid="audit-empty"/>
             ) : (
                <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="audit-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2">Fecha</th>
                                <th className="text-left px-3 py-2">Usuario</th>
                                <th className="text-left px-3 py-2">Rol</th>
                                <th className="text-left px-3 py-2">Acción</th>
                                <th className="text-left px-3 py-2">Entidad</th>
                                <th className="text-left px-3 py-2">IP</th>
                                <th className="px-3 py-2">Diff</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                                    <td className="px-3 py-2 font-mono text-xs text-slate-300">{fmtDateTime(r.created_at)}</td>
                                    <td className="px-3 py-2 text-xs text-slate-300">{r.actor_email || "—"}</td>
                                    <td className="px-3 py-2">
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border bg-slate-800 text-slate-300 border-slate-700">{r.actor_role || "—"}</span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${ACTION_COLORS[r.action] || ACTION_COLORS.other}`}>{r.action}</span>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="text-slate-200">{r.entity_type}</div>
                                        <div className="text-slate-500 font-mono">{r.entity_label || (r.entity_id ? r.entity_id.slice(0,8) : "—")}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.ip || "—"}</td>
                                    <td className="px-3 py-2 text-center">
                                        {(r.diff && Object.keys(r.diff).length > 0) || (r.metadata && Object.keys(r.metadata).length > 0) ? (
                                            <button type="button" onClick={() => setDiffOpen(r)} data-testid={`audit-diff-${r.id}`}
                                                className="text-xs text-brand-orange hover:underline">Ver</button>
                                        ) : <span className="text-slate-600">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
             )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between text-xs text-slate-400" data-testid="audit-pagination">
                    <span>Página {page} de {totalPages} · {total} registros</span>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                            data-testid="audit-prev" className="h-8 px-3 rounded border border-slate-700 bg-slate-950 disabled:opacity-40">← Anterior</button>
                        <button type="button" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
                            data-testid="audit-next" className="h-8 px-3 rounded border border-slate-700 bg-slate-950 disabled:opacity-40">Siguiente →</button>
                    </div>
                </div>
            )}

            {/* Diff modal */}
            {diffOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="audit-diff-modal">
                    <div className="w-full max-w-3xl bg-slate-900 rounded-2xl border border-slate-700 p-5 space-y-3 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-baseline justify-between">
                            <h2 className="font-display text-lg font-bold">Detalle</h2>
                            <button type="button" onClick={() => setDiffOpen(null)} data-testid="audit-diff-close" className="text-slate-400 hover:text-slate-200"><X size={18}/></button>
                        </div>
                        <div className="text-xs text-slate-400">
                            {diffOpen.actor_email} · {diffOpen.action} · {diffOpen.entity_type}
                            {diffOpen.entity_label && <> · <span className="font-mono">{diffOpen.entity_label}</span></>}
                        </div>
                        <AuditDiffView diff={diffOpen.diff} metadata={diffOpen.metadata}/>
                    </div>
                </div>
            )}
        </div>
    );
}
